import { Observable, from, of } from "rxjs"
import { mergeMap, switchMap, toArray } from "rxjs/operators"

import { DataStoreOptions, Item, SearchQuery, SearchResponse, SearchResult, Wemi } from "@skinsoft/types"
import { ItemDataStore } from "@skinsoft/entity"
import { WemiUtils } from "@skinsoft/wemi-utils";

/**
 * This class is not a real DataStore (does not inherit from DataStore).
 * It serves as an entrypoint (interface) to work with request interacting with Wemi.
 * The goal is to avoid circular dependencies in Work, Expression, Manifestation 
 * and ItemDataStore, it also allow to reduce coupling.
 */
export class WemiDataStore {
    public static getInstance() {
        if (!WemiDataStore.instance) {
            WemiDataStore.instance = new WemiDataStore();
        }

        return WemiDataStore.instance;
    }

    private static instance: WemiDataStore = null as any;
    
    public searchAll(searchQuery: SearchQuery, pathAndValueToCheck?: any, searchEndpointOverride: string | null = null, 
        options: DataStoreOptions = {}, onlyOnLocallyCreated: boolean = false, forceSearchTextId: boolean = false)
        : Observable<SearchResponse<SearchResult | Wemi>> {
        /**
         * In offline mode ItemDataStore.searchAll returns SearchResponse<Item> instead of 
         * SearchResponse<SearchResult | Wemi>. To do so it would require to have access to 
         * Work, Expression and Manifestation's offlineDataFetcher in ItemDataStore, this would 
         * increase coupling. Using WemiUtils directly in ItemDataStore is bad (it is actually done tough), 
         * because it create circular dependencies between Work, Expression, Manifestation and Item DataStores
         */

        let search$ = ItemDataStore.getInstance().searchAll(
            {
                queryText: searchQuery.queryText, 
                sortObject: searchQuery.sortObject, 
                pageSize: searchQuery.pageSize ?? 15, 
                types: searchQuery.types, 
                page: searchQuery.page ?? 0, 
                advancedSearches: searchQuery.advancedSearches, 
                filteredIds: searchQuery.filteredIds, 
                excludedIds: searchQuery.excludedIds,
                language: searchQuery.language,
            },
            pathAndValueToCheck, 
            searchEndpointOverride, 
            options, 
            onlyOnLocallyCreated, 
            forceSearchTextId, 
        )

        if(!window.navigator.onLine) {
            search$ = search$.pipe(
                switchMap(searchResponse => {
                    if(searchResponse.allData) {
                        return from(searchResponse.allData as Item[])
                    }
                    return from([] as Item[])
                }),
                mergeMap(item => {
                    return WemiUtils.getOrCreateWemiFromItemOffline(item.id)
                }),
                toArray(),
                switchMap(wemis => {
                    return of({
                        totalSize: wemis.length,
                        allData: wemis,
                        results: wemis,
                    })
                })
            )
        }

        return search$
    }
}